import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, absUrl } from "@/lib/api";
import EnquiryCountBadge from "@/components/supplier/EnquiryCountBadge";
import MissingImagePrompt from "@/components/supplier/MissingImagePrompt";
import ProfileSetupModal from "@/components/supplier/ProfileSetupModal";
import ProfileCompletenessNudge from "@/components/supplier/ProfileCompletenessNudge";
import ChecklistCelebration from "@/components/dashboard/ChecklistCelebration";
import WelcomeTour from "@/components/supplier/WelcomeTour";
import Sparkline from "@/components/Sparkline";
import { listingPath } from "@/lib/paths";
import { Plus, Sparkles, Loader2, Mail, Inbox, Star, Trash2, Image as ImageIcon, Upload, Check, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import BulkUploadModal from "@/components/BulkUploadModal";
import ServiceRegionsField from "@/components/ServiceRegionsField";
import ChangePlanModal from "@/components/dashboard/ChangePlanModal";
import CancelPlanModal from "@/components/dashboard/CancelPlanModal";
import StatCard from "@/components/dashboard/StatCard";
import ClaimedChecklist, { checklistSteps } from "@/components/dashboard/ClaimedChecklist";
import ViewInsightsPanel from "@/components/dashboard/ViewInsightsPanel";
import EmailPreferences from "@/components/EmailPreferences";

export function ListingForm({ listing, onClose, onSaved, brands = [] }) {
  const isEdit = !!listing;
  const [tree, setTree] = useState([]);
  const [form, setForm] = useState(() => {
    if (listing) {
      // Pre-fill from existing listing. `categories` array is authoritative; fall back to legacy `category_id`.
      const cats = (listing.categories && listing.categories.length > 0)
        ? listing.categories
        : (listing.category_id ? [listing.category_id] : []);
      return {
        title: listing.title || "",
        description: listing.description || "",
        category_id: cats[0] || "",
        categories: cats,
        supplier_type: listing.supplier_type || "",
        price_min: listing.price_min ?? "",
        price_max: listing.price_max ?? "",
        currency: listing.currency || "USD",
        image_url: listing.image_url || "",
        logo_url: listing.logo_url || "",
        gallery: listing.gallery || [],
        lead_time: listing.lead_time || "",
        moq: listing.moq || "",
        tags: (listing.tags || []).join(", "),
        is_verified: !!listing.is_verified,
        custom_oem: !!listing.custom_oem,
        city: listing.city || "",
        country: listing.country || "",
        country_code: listing.country_code || "",
        state_codes: listing.state_codes || [],
        nationwide: !!listing.nationwide,
        global: !!listing.global,
        countries: Array.isArray(listing.countries) && listing.countries.length > 0
          ? listing.countries
          : (listing.country_code
              ? [{ code: listing.country_code, name: listing.country || listing.country_code, states: listing.state_codes || [], nationwide: !!listing.nationwide }]
              : []),
        lat: listing.lat ?? "",
        lng: listing.lng ?? "",
        contact_email: listing.contact_email || "",
        contact_phone: listing.contact_phone || "",
        website: listing.website || "",
        brand_profile_id: listing.brand_profile_id || "",
      };
    }
    return {
      title: "", description: "", category_id: "", categories: [], supplier_type: "", price_min: "", price_max: "", currency: "USD",
      image_url: "", logo_url: "", gallery: [], lead_time: "", moq: "", tags: "", is_verified: false, custom_oem: false,
      city: "", country: "", country_code: "", state_codes: [], nationwide: false, global: false, countries: [], lat: "", lng: "",
      contact_email: "", contact_phone: "", website: "",
      brand_profile_id: "",
    };
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/categories/tree").then(({ data }) => setTree(data)); }, []);
  const [initialForm] = useState(() => JSON.stringify(form));
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const dirty = JSON.stringify(form) !== initialForm;
  const requestClose = () => { if (dirty) setConfirmDiscard(true); else onClose(); };

  const toggleCategory = (id) => {
    setForm((f) => {
      const has = f.categories.includes(id);
      const nextCats = has ? f.categories.filter((c) => c !== id) : [...f.categories, id];
      return { ...f, categories: nextCats, category_id: nextCats[0] || "" };
    });
  };

  const handleImageUpload = async (e, target = "image_url") => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "gallery") {
        setForm((f) => ({ ...f, gallery: [...(f.gallery || []), data.url] }));
      } else {
        setForm((f) => ({ ...f, [target]: data.url }));
      }
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = ""; // let user re-select the same file if needed
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.categories.length === 0) { toast.error("Select at least one category"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        category_id: form.categories[0],  // legacy primary
        categories: form.categories,       // multi-cat array (backend supports)
        price: null,
        price_min: form.price_min ? Number(form.price_min) : null,
        price_max: form.price_max ? Number(form.price_max) : null,
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
        tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : (form.tags || []),
        state_codes: (form.state_codes || []).map((s) => (s || "").toUpperCase()).filter(Boolean),
        nationwide: !!form.nationwide,
        global: !!form.global,
        countries: (form.countries || []).map((c) => ({
          code: (c.code || "").toUpperCase(),
          name: c.name || "",
          states: (c.states || []).map((s) => (s || "").toUpperCase()).filter(Boolean),
          nationwide: !!c.nationwide,
        })),
        brand_profile_id: form.brand_profile_id || null,
      };
      if (isEdit) {
        await api.put(`/listings/${listing.id}`, payload);
        toast.success("Listing updated");
      } else {
        await api.post("/listings", payload);
        toast.success("Listing created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="listing-form-modal">
      {confirmDiscard && (
        <div className="fixed inset-0 z-[60] bg-[#11211A]/40 flex items-center justify-center p-4" onClick={() => setConfirmDiscard(false)} data-testid="listing-form-discard-prompt">
          <div className="bg-white w-full max-w-sm p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="font-serif text-xl text-[#11211A]">Discard unsaved changes?</div>
            <p className="text-sm text-[#4A5550] mt-2">You have edits that haven't been saved. Close anyway and lose them?</p>
            <div className="flex justify-end gap-2 pt-5">
              <button type="button" onClick={() => setConfirmDiscard(false)} className="btn-ghost text-xs" data-testid="listing-form-discard-keep">Keep editing</button>
              <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.15em] px-4 py-2 bg-[#B35C4A] text-white hover:bg-[#994A3B] transition-colors" data-testid="listing-form-discard-confirm">Discard changes</button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-[#E8ECE9] flex items-center justify-between flex-shrink-0">
          <div>
            <div className="tracking-overline">{isEdit ? "Edit listing" : "New listing"}</div>
            <div className="font-serif text-xl text-[#11211A]">{isEdit ? listing.title : "Create a new listing"}</div>
          </div>
          <button type="button" onClick={requestClose} className="text-[#7D8C85] text-2xl leading-none" data-testid="listing-form-close">×</button>
        </div>
        <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
          <div className="p-6 grid grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0" data-testid="listing-form-scroll-body">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Title</label>
            <input required value={form.title} onChange={set("title")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="listing-title" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#4A5550] mb-1">About Your Organisation</label>
            <textarea
              required
              rows={3}
              value={form.description}
              onChange={set("description")}
              placeholder="Tell buyers who you are, what you supply, and why hotels choose you. Keep it to 2-3 sentences."
              className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm"
              data-testid="listing-description"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#4A5550] mb-2">
              Categories <span className="text-[#7D8C85] font-normal">— pick one or more (multi-select, includes pre-launch categories)</span>
            </label>
            <div className="border border-[#C4CFC9] rounded-sm p-3 space-y-3 max-h-72 overflow-y-auto" data-testid="listing-categories-multiselect">
              <div className="flex flex-wrap gap-2">
                {tree.filter((p) => !(p.children || []).some((c) => c.group)).map((p) => {
                  const active = form.categories.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleCategory(p.id)}
                      className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 border transition-colors ${active ? "bg-[#264839] border-[#264839] text-white" : "bg-white border-[#C4CFC9] text-[#4A5550] hover:border-[#264839] hover:text-[#264839]"}`}
                      data-testid={`category-chip-${p.id}`}
                      aria-pressed={active}
                    >
                      {active && <Check className="w-3 h-3" />}
                      {p.name}
                      {p.is_coming_soon && (
                        <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 border ${active ? "border-white/40 text-white/80" : "border-[#B35C4A]/40 text-[#B35C4A]"}`}>
                          Pre-launch
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Grouped subcategory picker (restructured parents) — tick as many as apply; it stays one listing */}
              {tree.filter((p) => (p.children || []).some((c) => c.group)).map((p) => {
                const byGroup = {};
                (p.children || []).forEach((c) => { (byGroup[c.group || "Other"] ||= []).push(c); });
                const picked = (p.children || []).filter((c) => form.categories.includes(c.id)).length;
                return (
                  <div key={p.id + "-grouped"} className="border-t border-[#E8ECE9] pt-3" data-testid={`subcategory-picker-${p.id}`}>
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <div className="text-xs font-medium text-[#11211A]">{p.name}</div>
                      <div className="text-[11px] text-[#7D8C85]" data-testid={`subcategory-picked-count-${p.id}`}>{picked ? `${picked} ticked` : "Tick every subcategory you supply"}</div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                      {Object.entries(byGroup).map(([g, cats]) => (
                        <div key={g}>
                          <div className="tracking-overline mb-1.5">{g}</div>
                          <div className="space-y-1">
                            {cats.map((s) => {
                              const active = form.categories.includes(s.id);
                              return (
                                <label key={s.id} className={`flex items-center gap-2 text-xs px-2 py-1 cursor-pointer rounded-sm transition-colors ${active ? "bg-[#264839]/5 text-[#11211A]" : "text-[#4A5550] hover:bg-[#F0EFEA]"}`}>
                                  <input type="checkbox" checked={active} onChange={() => toggleCategory(s.id)} className="accent-[#264839]" data-testid={`subcategory-check-${s.id}`} />
                                  {s.name}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {/* Nested legacy sub-cats — kept for backwards-compat if any parent still has children */}
              {tree.some((p) => (p.children || []).length > 0 && !(p.children || []).some((c) => c.group)) && (
                <div className="border-t border-[#E8ECE9] pt-3 space-y-3">
                  {tree.filter((p) => (p.children || []).length > 0 && !(p.children || []).some((c) => c.group)).map((p) => (
                    <div key={p.id + "-legacy"}>
                      <div className="tracking-overline mb-2">{p.name} legacy sub-categories</div>
                      <div className="flex flex-wrap gap-2">
                        {(p.children || []).map((s) => {
                          const active = form.categories.includes(s.id);
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => toggleCategory(s.id)}
                              className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 border transition-colors ${active ? "bg-[#264839] border-[#264839] text-white" : "bg-white border-[#C4CFC9] text-[#4A5550] hover:border-[#264839] hover:text-[#264839]"}`}
                              data-testid={`category-chip-${s.id}`}
                              aria-pressed={active}
                            >
                              {active && <Check className="w-3 h-3" />} {s.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="text-xs text-[#7D8C85] mt-1.5">
              {form.categories.length === 0
                ? "Pick at least one category to continue."
                : `${form.categories.length} categor${form.categories.length === 1 ? "y" : "ies"} selected.`}
            </div>
          </div>
          {brands.length > 0 && (
            <div className="col-span-2" data-testid="listing-brand-field">
              <label className="block text-xs font-medium text-[#4A5550] mb-1">
                Brand <span className="text-[#7D8C85] font-normal">(Enterprise — attach this listing to one of your brand profiles)</span>
              </label>
              <select value={form.brand_profile_id} onChange={set("brand_profile_id")} className="w-full px-3 py-2 border border-[#C4CFC9] bg-white rounded-sm" data-testid="listing-brand-select">
                <option value="">— No brand (uses main supplier profile) —</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Price range — min (USD)</label>
            <input type="number" step="0.01" value={form.price_min} onChange={set("price_min")} placeholder="e.g. 10" className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="listing-price-min" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Price range — max (USD)</label>
            <input type="number" step="0.01" value={form.price_max} onChange={set("price_max")} placeholder="e.g. 15" className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="listing-price-max" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Website</label>
            <input value={form.website} onChange={set("website")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Lead time <span className="text-[#7D8C85] font-normal">(give a range)</span></label>
            <input value={form.lead_time} onChange={set("lead_time")} placeholder="e.g. 2–4 weeks" title="Provide a range (e.g. 2–4 weeks). Avoid 'on request'." className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="listing-lead-time" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">MOQ <span className="text-[#7D8C85] font-normal">(give a range)</span></label>
            <input value={form.moq} onChange={set("moq")} placeholder="e.g. from 100 units" title="Provide a range or floor (e.g. 'from 100 units'). Avoid 'on request'." className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="listing-moq" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-[#264839] mb-1 uppercase tracking-wide">Tags <span className="text-[#4A5550] font-normal normal-case tracking-normal">/ attributes</span></label>
            <input
              value={form.tags}
              onChange={set("tags")}
              placeholder="cotton, oeko-tex, extra-large, hypoallergenic"
              className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm"
              data-testid="listing-tags"
            />
            <div className="text-xs text-[#7D8C85] mt-1">Comma-separated <strong className="text-[#264839]">tags</strong>. Include material, size, certifications, or any keyword buyers might search for.</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">City</label>
            <input value={form.city} onChange={set("city")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="listing-city" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Supplier type</label>
            <select value={form.supplier_type} onChange={set("supplier_type")} className="w-full px-3 py-2 border border-[#C4CFC9] bg-white rounded-sm" data-testid="listing-supplier-type">
              <option value="">— Select —</option>
              <option value="Manufacturer">Manufacturer</option>
              <option value="Wholesaler">Wholesaler</option>
            </select>
          </div>
          <label className="flex items-start gap-2.5 p-3 border border-[#E8ECE9] bg-[#FAFAF7] cursor-pointer sm:col-span-2" data-testid="listing-custom-oem-label">
            <input type="checkbox" checked={!!form.custom_oem} onChange={(e) => setForm((f) => ({ ...f, custom_oem: e.target.checked }))} className="accent-[#264839] mt-0.5" data-testid="listing-custom-oem" />
            <span>
              <span className="block text-sm font-medium text-[#11211A]">Custom / OEM available</span>
              <span className="block text-[11px] text-[#7D8C85]">Shows a “Custom / OEM” label on the card and includes this supplier in the buyer Custom / OEM filter.</span>
            </span>
          </label>

          <ServiceRegionsField
            value={{
              global: form.global,
              countries: form.countries,
              // legacy mirror for old listings
              country_name: form.country,
              country_code: form.country_code,
              state_codes: form.state_codes,
              nationwide: form.nationwide,
            }}
            onChange={(next) => setForm({
              ...form,
              global: !!next.global,
              countries: next.countries || [],
              // Keep legacy fields synced (mirrors first country) so category/search filters keep working
              country: next.country_name || "",
              country_code: next.country_code || "",
              state_codes: next.state_codes || [],
              nationwide: !!next.nationwide,
            })}
          />
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Contact email</label>
            <input type="email" value={form.contact_email} onChange={set("contact_email")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Contact phone</label>
            <input value={form.contact_phone} onChange={set("contact_phone")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" />
          </div>

          <div className="col-span-2 border-t border-[#E8ECE9] pt-4 mt-2">
            <label className="block text-xs font-medium text-[#4A5550] mb-2">
              Company logo <span className="text-[#7D8C85] font-normal">— shown on your profile and listing card (PNG / JPEG / WebP, square works best)</span>
            </label>
            <div className="flex items-center gap-3">
              {form.logo_url ? (
                <div className="w-20 h-20 border border-[#E8ECE9] bg-white flex items-center justify-center relative" data-testid="listing-logo-preview">
                  <img src={absUrl(form.logo_url)} alt="logo" className="max-w-full max-h-full object-contain" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, logo_url: "" }))} className="absolute -top-2 -right-2 bg-white border border-[#E8ECE9] text-[#B35C4A] text-xs w-5 h-5 rounded-full" data-testid="listing-logo-remove">×</button>
                </div>
              ) : (
                <div className="w-20 h-20 border border-dashed border-[#C4CFC9] flex items-center justify-center text-[#C4CFC9] text-[10px] uppercase tracking-wider">No logo</div>
              )}
              <label className={`btn-secondary text-xs cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="listing-logo-upload-btn">
                <Upload className="w-3.5 h-3.5" /> {form.logo_url ? "Replace logo" : "Upload logo"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleImageUpload(e, "logo_url")} data-testid="listing-logo-input" />
              </label>
            </div>
          </div>

          <div className="col-span-2 border-t border-[#E8ECE9] pt-4 mt-2">
            <label className="block text-xs font-medium text-[#4A5550] mb-2">
              Product photos <span className="text-[#7D8C85] font-normal">— upload your own (JPEG / PNG / WebP, up to 10MB each)</span>
            </label>
            <div className="flex flex-wrap gap-3 items-start">
              {form.image_url && (
                <div className="w-32 h-32 border border-[#E8ECE9] overflow-hidden relative group" data-testid="listing-primary-preview">
                  <img src={absUrl(form.image_url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="absolute top-1 right-1 bg-white/80 hover:bg-white text-[#B35C4A] text-xs px-1.5 py-0.5 rounded" data-testid="listing-primary-remove">×</button>
                  <div className="absolute bottom-0 left-0 right-0 bg-[#264839] text-white text-[10px] uppercase tracking-wider py-0.5 text-center">Primary</div>
                </div>
              )}
              {(form.gallery || []).map((url, idx) => (
                <div key={idx} className="w-32 h-32 border border-[#E8ECE9] overflow-hidden relative group" data-testid={`listing-gallery-preview-${idx}`}>
                  <img src={absUrl(url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, i) => i !== idx) }))} className="absolute top-1 right-1 bg-white/80 hover:bg-white text-[#B35C4A] text-xs px-1.5 py-0.5 rounded">×</button>
                </div>
              ))}
              <label className={`w-32 h-32 border-2 border-dashed border-[#C4CFC9] hover:border-[#264839] flex flex-col items-center justify-center text-[#7D8C85] hover:text-[#264839] cursor-pointer transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="listing-upload-tile">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                <div className="text-[10px] uppercase tracking-wider mt-2">{uploading ? "Uploading" : (form.image_url ? "Add photo" : "Primary photo")}</div>
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleImageUpload(e, form.image_url ? "gallery" : "image_url")} data-testid="listing-file-input" />
              </label>
            </div>
            <p className="text-[10px] text-[#7D8C85] mt-2 italic">
              First upload becomes the primary image. Subsequent uploads go into the gallery. Real photos rank better and convert stronger than stock imagery.
            </p>
          </div>

          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#E8ECE9] bg-white flex-shrink-0" data-testid="listing-form-footer">
            <button type="button" onClick={requestClose} className="btn-ghost" data-testid="listing-form-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="listing-save">{saving ? "Saving…" : (isEdit ? "Save changes" : "Create listing")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [products, setProducts] = useState([]);
  const [limits, setLimits] = useState(null);
  const [profileViews, setProfileViews] = useState(null);
  const [viewInsights, setViewInsights] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [credits, setCredits] = useState(null);
  const [leaderPerf, setLeaderPerf] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [supplierOnboarding, setSupplierOnboarding] = useState(null);
  const [activeBrandId, setActiveBrandId] = useState(() => localStorage.getItem("as_active_brand") || "");
  const [brands, setBrands] = useState([]);
  const [tab, setTab] = useState(() => (searchParams.get("tab") === "email-prefs" ? "email-prefs" : "listings"));
  const [showForm, setShowForm] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [pricing, setPricing] = useState([]);

  const markRfqReplied = async (r) => {
    try {
      const { data } = await api.post(`/rfqs/${r.id}/mark-replied`);
      setRfqs((rows) => rows.map((x) => (x.id === r.id ? { ...x, replied_at: new Date().toISOString(), response_hours: data.response_hours ?? null } : x)));
      toast.success("Marked as replied — thanks, that keeps your response time accurate");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not mark as replied"); }
  };

  const load = async () => {
    if (!user) return;
    try {
      const [ls, rq, pr, lim, pd, an] = await Promise.all([
        api.get(`/listings?advertiser_id=${user.id}&limit=100`),
        api.get("/rfqs"),
        api.get("/pricing"),
        api.get("/me/limits"),
        api.get(`/products?supplier_id=${user.id}&limit=100`),
        api.get("/supplier/analytics"),
      ]);
      setListings(ls.data.items);
      setRfqs(rq.data);
      setPricing(pr.data);
      setLimits(lim.data);
      setProducts(pd.data.items || []);
      setAnalytics(an.data);
      api.get("/supplier/profile").then(({ data }) => setProfile(data)).catch(() => {});
      // Iter85: monthly profile views (shown in the free-tier banner)
      try {
        const pv = await api.get("/supplier/profile-views");
        setProfileViews(pv.data);
      } catch { /* non-critical */ }
      // Iter87: category view insights (paid tiers only — free 403s)
      try {
        const vi = await api.get("/supplier/view-insights");
        setViewInsights(vi.data);
      } catch { setViewInsights(null); }
      // Fire quote credits + brand profiles requests separately (tier-gated features)
      try {
        const cr = await api.get("/supplier/quote-credits");
        setCredits(cr.data);
      } catch { /* free tier can 403 — ignore */ }
      // Iter42: leader performance (impressions + position 1 time + monthly guarantee)
      try {
        const lp = await api.get("/supplier/leader-performance");
        setLeaderPerf(lp.data);
      } catch { /* free tier can 403 — ignore */ }
      // Iter43: Enterprise onboarding status
      try {
        const ob = await api.get("/supplier/enterprise/onboarding");
        setOnboarding(ob.data);
      } catch { /* non-enterprise 403 — ignore */ }
      // Iter56: general supplier onboarding checklist (all tiers)
      try {
        const so = await api.get("/onboarding/status");
        setSupplierOnboarding(so.data);
      } catch { /* buyers 403 — ignore */ }
      if (lim.data?.can_add_brand_profile) {
        try {
          const bp = await api.get("/brand-profiles");
          setBrands(bp.data || []);
        } catch { /* non-enterprise falls back to empty list */ }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Your session has expired. Please sign in again.");
        navigate("/login");
      } else {
        const d = err.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Failed to load dashboard data");
      }
    }
  };

  useEffect(() => {
    if (!loading && !user) {
      const upgrade = searchParams.get("upgrade");
      navigate(upgrade ? `/register?upgrade=${encodeURIComponent(upgrade)}` : "/login");
    } else {
      load();
    }
    // eslint-disable-next-line
  }, [user, loading]);

  // Iter69 · Poll onboarding-call status every 15s while the call is unbooked
  // so the badge auto-refreshes the moment Calendly's webhook fires — no page
  // reload needed. Stops polling as soon as booked=true.
  useEffect(() => {
    if (!user || user.role === "buyer") return;
    if (supplierOnboarding?.onboarding_call?.booked) return; // already booked → no need to poll
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/onboarding/status");
        setSupplierOnboarding(data);
        if (data?.onboarding_call?.booked) {
          toast.success("Your onboarding call is booked — see you soon!");
          clearInterval(iv);
        }
      } catch { /* silent */ }
    }, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [user, supplierOnboarding?.onboarding_call?.booked]);

  // Auto-launch Stripe subscription checkout when Pricing CTA lands here with ?upgrade=<tier>
  useEffect(() => {
    const wantsUpgrade = searchParams.get("upgrade");
    if (!wantsUpgrade || !user || !limits) return;
    const validTiers = ["starter", "growth", "leader", "enterprise"];
    if (!validTiers.includes(wantsUpgrade)) {
      setSearchParams({}, { replace: true });
      return;
    }
    // Consume the param immediately so a refresh doesn't retrigger checkout.
    setSearchParams({}, { replace: true });
    (async () => {
      try {
        // Iter80 · replay the promo code the visitor entered on /pricing before registering
        let storedPromo = "";
        try { storedPromo = sessionStorage.getItem("as_promo_code") || ""; } catch {}
        const { data } = await api.post("/payments/subscribe", {
          tier: wantsUpgrade,
          origin_url: window.location.origin,
          promo_code: storedPromo || undefined,
        });
        try { sessionStorage.removeItem("as_promo_code"); } catch {}
        window.location.href = data.url;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Checkout failed — please try again from the Pricing page.");
      }
    })();
    // eslint-disable-next-line
  }, [user, limits, searchParams]);

  // Handle subscription callback: poll Stripe → sync tier + subscription_status.
  useEffect(() => {
    const ssid = searchParams.get("subscription_session_id");
    if (!ssid || !user) return;
    setSearchParams({}, { replace: true });
    (async () => {
      for (let i = 0; i < 10; i++) {
        try {
          const { data } = await api.get(`/payments/subscription-status/${ssid}`);
          if (data.payment_status === "paid" && ["active", "trialing"].includes(data.subscription_status)) {
            toast.success(`Subscription active — welcome to ${data.tier}!`);
            load();
            return;
          }
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 3000));
      }
      toast.info("Payment received — your plan will update shortly.");
      load();
    })();
    // eslint-disable-next-line
  }, [user, searchParams]);

  // Handle credit-pack Stripe callback: poll status, refresh balance on success.
  useEffect(() => {
    const csid = searchParams.get("credit_session_id");
    const canceled = searchParams.get("credits_canceled");
    const autoreload = searchParams.get("autoreload");
    const arPackId = searchParams.get("pack_id");
    if (canceled) {
      toast.info("Credit purchase canceled.");
      setSearchParams({}, { replace: true });
      return;
    }
    // Deep link from the low-balance nudge email: auto-launch Stripe checkout for that pack.
    if (autoreload === "1" && arPackId && user) {
      setSearchParams({}, { replace: true });
      (async () => {
        try {
          const { data } = await api.post("/payments/credit-checkout", {
            pack_id: arPackId,
            origin_url: window.location.origin,
          });
          window.location.href = data.url;
        } catch (e) {
          toast.error(e.response?.data?.detail || "Auto-reload checkout failed — please use Manual Top-Up.");
          setTab("credits");
        }
      })();
      return;
    }
    if (!csid || !user) return;
    setSearchParams({}, { replace: true });
    (async () => {
      // Poll for up to ~30s for the webhook to credit the account.
      for (let i = 0; i < 10; i++) {
        try {
          const { data } = await api.get(`/payments/status/${csid}`);
          if (data.payment_status === "paid") {
            toast.success("Credits added to your account.");
            const cr = await api.get("/supplier/quote-credits");
            setCredits(cr.data);
            return;
          }
        } catch { /* transient — keep polling */ }
        await new Promise((r) => setTimeout(r, 3000));
      }
      toast.info("Payment received — credits will appear shortly.");
    })();
    // eslint-disable-next-line
  }, [user, searchParams]);

  const upgrade = async (listingId, tier) => {
    try {
      const { data } = await api.post("/payments/subscribe", {
        tier,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    }
  };

  const openBillingPortal = async () => {
    try {
      const { data } = await api.post("/payments/billing-portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal");
    }
  };

  // Iter60: Change Plan + Cancel Plan self-serve
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showCancelPlan, setShowCancelPlan] = useState(false);
  const [busyPlan, setBusyPlan] = useState(false);

  const doChangePlan = async (tier) => {
    setBusyPlan(true);
    try {
      await api.post("/payments/change-plan", { tier });
      toast.success(`Plan changed to ${tier}. Stripe will prorate the difference on your next invoice.`);
      setShowChangePlan(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Change plan failed");
    } finally { setBusyPlan(false); }
  };

  const doCancelPlan = async (reason, feedback) => {
    setBusyPlan(true);
    try {
      const { data } = await api.post("/payments/cancel-plan", { reason, feedback });
      const end = (data.period_end || "").split("T")[0];
      toast.success(`Plan set to cancel on ${end || "period end"}. Your listing stays live until then.`);
      setShowCancelPlan(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally { setBusyPlan(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this listing? It disappears from the directory immediately. If this was a mistake, contact hello@accommodationsuppliers.com within 30 days and we can restore it.")) return;
    await api.delete(`/listings/${id}`);
    toast.success("Listing deleted");
    load();
  };

  const delProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  if (loading || !user) return <div className="max-w-7xl mx-auto px-4 py-16">Loading…</div>;

  const showsClaimedChecklist = limits?.tier === "free" && user?.role !== "admin" && !user?.is_unclaimed && listings.length > 0;
  const claimedChecklistDone = !showsClaimedChecklist || checklistSteps(listings, limits.tier, profile).every((s) => s.done);
  const celebrateEligible = !!limits && user?.role !== "admin" && !user?.is_unclaimed && listings.length > 0 && !!profile;
  const allChecklistStepsDone = celebrateEligible && checklistSteps(listings, limits.tier, profile).every((s) => s.done);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="tracking-overline mb-3">Supplier dashboard</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#11211A]">Welcome, {user.name}</h1>
          <p className="text-sm text-[#7D8C85] mt-2">{user.company || user.email}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {brands.length > 0 && (
            <BrandSwitcher brands={brands} activeId={activeBrandId} onChange={(id) => { setActiveBrandId(id); localStorage.setItem("as_active_brand", id || ""); }} />
          )}
          {user?.role !== "admin" && (
            <button onClick={() => setShowProfile(true)} className="btn-ghost" data-testid="edit-profile-btn">
              <UserCircle2 className="w-4 h-4" /> Edit profile
            </button>
          )}
          <button onClick={() => setShowBulk(true)} className="btn-ghost" data-testid="bulk-upload-btn">
            <Upload className="w-4 h-4" /> Bulk upload
          </button>
          <button onClick={() => { setEditListing(null); setShowForm(true); }} disabled={limits && !limits.can_add_listing} title={limits && !limits.can_add_listing ? "Category listing limit reached — upgrade to add more." : ""} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="new-listing-btn">
            <Plus className="w-4 h-4" /> New listing
          </button>
        </div>
      </div>

      {onboarding && onboarding.status === "incomplete" && (
        <EnterpriseWelcomeModal onboarding={onboarding} />
      )}

      {(showProfile || (user?.profile_setup_pending && user?.role !== "admin")) && (
        <ProfileSetupModal
          firstTime={!showProfile && !!user?.profile_setup_pending}
          onClose={() => { setShowProfile(false); setUser({ ...user, profile_setup_pending: false }); }}
          onSaved={() => { setShowProfile(false); setUser({ ...user, profile_setup_pending: false }); load(); }}
        />
      )}

      {user?.role !== "admin" && user?.welcome_tour_pending && !user?.profile_setup_pending && !showProfile && limits && (
        <WelcomeTour onDone={() => setUser({ ...user, welcome_tour_pending: false })} />
      )}

      {user?.role !== "admin" && !user?.profile_setup_pending && <ProfileCompletenessNudge profile={profile} onEditProfile={() => setShowProfile(true)} />}

      <MissingImagePrompt user={user} listings={listings} products={products} onUpload={() => { setEditListing(listings[0]); setShowForm(true); }} />

      {/* Iter56 · Supplier onboarding nudge (all paid tiers) */}
      {/* "Setup complete" only when every checklist (onboarding + claimed-supplier steps) is genuinely done. Free suppliers see only the 3-step claimed checklist. */}
      {supplierOnboarding && limits?.tier !== "free" && (
        supplierOnboarding.progress?.complete ? (claimedChecklistDone && (
          <div className="mb-4 bg-[#264839] text-white px-5 py-3 flex items-center gap-3" data-testid="onboarding-nudge-complete">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 flex-shrink-0">✓</span>
            <div className="flex-1 text-sm">
              <span className="tracking-overline text-[10px] text-[#F0EFEA] opacity-80 mr-2">Setup complete</span>
              Your listing is polished and ready — buyers can find you today.
            </div>
            <Link to="/onboarding" className="text-[10px] uppercase tracking-widest font-semibold text-white/90 hover:text-white underline" data-testid="onboarding-nudge-review-link">
              Review
            </Link>
          </div>
        )) : (
          <Link to="/onboarding" className="mb-4 bg-white border-l-4 border-[#B35C4A] px-5 py-3 flex items-center gap-3 hover:bg-[#F0EFEA] transition group" data-testid="onboarding-nudge">
            <div className="flex-1">
              <div className="tracking-overline text-[11px] text-[#B35C4A]">Finish your setup</div>
              <div className="text-sm text-[#11211A] mt-0.5">
                <strong>{supplierOnboarding.progress.done} of {supplierOnboarding.progress.total} steps done</strong> — complete your onboarding checklist to unlock full visibility.
              </div>
              <div className="mt-2 h-1.5 bg-[#F0EFEA] w-full max-w-md overflow-hidden">
                <div className="h-full bg-[#B35C4A] transition-all" style={{ width: `${supplierOnboarding.progress.percent}%` }} data-testid="onboarding-nudge-progress" />
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#B35C4A] group-hover:text-[#264839] transition flex-shrink-0">
              Continue →
            </div>
          </Link>
        )
      )}

      {/* Iter56 · Enterprise-only "Complete Your Setup" CTA — hidden once onboarding_status=complete */}
      {limits?.tier === "enterprise" && onboarding && onboarding.status !== "complete" && (
        <Link
          to="/dashboard/enterprise-setup"
          className="mb-4 bg-[#B35C4A] text-white px-5 py-3 flex items-center gap-3 hover:bg-[#9E4E3E] transition"
          data-testid="enterprise-complete-setup-btn"
        >
          <span className="tracking-overline text-[10px] text-white/80">Enterprise white-glove</span>
          <span className="text-sm font-semibold flex-1">Complete Your Setup — {onboarding.step_completed || 0}/{onboarding.total_steps || 5} steps done</span>
          <span className="text-[10px] uppercase tracking-widest font-semibold">Open wizard →</span>
        </Link>
      )}

      {limits && limits.tier === "free" && user?.role !== "admin" && !user?.is_unclaimed && (
        <ClaimedChecklist listings={listings} tier={limits.tier} profile={profile} onEditProfile={() => setShowProfile(true)} />
      )}
      {celebrateEligible && <ChecklistCelebration userId={user.id} allDone={allChecklistStepsDone} />}
      {limits && limits.tier === "free" && user?.role !== "admin" && <EnquiryCountBadge />}
      {limits && limits.tier === "free" && user?.role !== "admin" && (
        <div className="mb-4 border-2 border-[#B8860B]/40 bg-gradient-to-r from-[#FFFBEB] to-[#F0EFEA] px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="free-tier-upgrade-banner">
          {profileViews && (
            <div className="shrink-0 text-center sm:text-left sm:pr-5 sm:border-r border-[#B8860B]/30" data-testid="free-profile-views">
              <div className="font-serif text-3xl text-[#11211A] leading-none" data-testid="free-profile-views-count">{profileViews.this_month}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#7D8C85] mt-1">profile view{profileViews.this_month === 1 ? "" : "s"} in {profileViews.month_label.split(" ")[0]}</div>
              {profileViews.last_month > 0 && <div className="text-[10px] text-[#9AA6A0]">{profileViews.last_month} last month</div>}
            </div>
          )}
          <div className="flex-1">
            <div className="tracking-overline text-[10px] text-[#B8860B]">You're on the Free listing</div>
            <div className="text-sm text-[#11211A] mt-0.5">
              {profileViews && profileViews.this_month > 0
                ? <><strong>{profileViews.this_month} buyer{profileViews.this_month === 1 ? "" : "s"} viewed your profile this month</strong> — but none can send you a quote request. Upgrade to Founding Supplier to receive matched RFQs, add product pages and rank above free listings.</>
                : <><strong>Upgrade to Founding Supplier</strong> to receive matched RFQs, add product pages and appear above free listings in every search.</>}
            </div>
          </div>
          <Link to="/pricing" className="btn-primary shrink-0" data-testid="free-tier-upgrade-cta">Upgrade to Founding Supplier</Link>
        </div>
      )}
      {limits && <PlanBanner limits={limits} credits={credits?.quote_credits} onBillingPortal={openBillingPortal} onChangePlan={() => setShowChangePlan(true)} onCancelPlan={() => setShowCancelPlan(true)} />}
      {showChangePlan && (
        <ChangePlanModal
          currentTier={limits?.tier}
          busy={busyPlan}
          onConfirm={doChangePlan}
          onClose={() => setShowChangePlan(false)}
        />
      )}
      {showCancelPlan && (
        <CancelPlanModal
          onClose={() => setShowCancelPlan(false)}
          onConfirm={doCancelPlan}
          onSaveOfferAccepted={load}
          saveOfferUsed={!!user?.save_offer_used_at}
          busy={busyPlan}
        />
      )}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="performance-dashboard">
          <StatCard label="Total views" value={analytics.total_views} sub={`Listings ${analytics.listing_views} · Products ${analytics.product_views}`} />
          <StatCard label="RFQs received" value={analytics.rfqs_received} sub={typeof analytics.rfqs_ships_to_match === "number" ? `${analytics.rfqs_ships_to_match} ships-to match` : `Across ${analytics.listing_count} listing${analytics.listing_count === 1 ? "" : "s"}`} />
          <StatCard label="Info clicks" value={analytics.info_clicks} sub="Card-level enquiries" />
          <StatCard label="Active products" value={analytics.product_count} sub="On individual product pages" />
        </div>
      )}
      {analytics && (analytics.in_homepage_rotation || analytics.spotlight_clicks > 0) && (
        <div className="mt-4 bg-[#11211A] text-white px-5 py-4 flex flex-wrap items-center gap-6 border-l-4 border-[#F0C866]" data-testid="spotlight-clicks-card">
          <div className="flex-1 min-w-[220px]">
            <div className="tracking-overline text-[10px] text-[#F0C866]">Homepage Fresh Picks</div>
            <div className="text-sm text-white/80 mt-1">Your featured position rotates on the homepage. These are real buyer clicks from that spot through to your listing.</div>
          </div>
          <div className="min-w-[160px]">
            <Sparkline series={analytics.spotlight_series_30d} width={160} height={40} stroke="#F0C866" fill="rgba(240,200,102,0.18)" testId="spotlight-clicks-sparkline" />
            <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">clicks per day · 30 days</div>
          </div>
          <div className="text-right">
            <div className="font-serif text-4xl leading-none" data-testid="spotlight-clicks-30d">{analytics.spotlight_clicks_30d}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">clicks · last 30 days</div>
            <div className="text-[11px] text-white/70 mt-1" data-testid="spotlight-clicks-30d-devices">{analytics.spotlight_clicks_mobile_30d} mobile · {analytics.spotlight_clicks_desktop_30d} desktop</div>
          </div>
          <div className="text-right">
            <div className="font-serif text-4xl leading-none" data-testid="spotlight-clicks-total">{analytics.spotlight_clicks}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">clicks · all time</div>
            <div className="text-[11px] text-white/70 mt-1" data-testid="spotlight-clicks-total-devices">{analytics.spotlight_clicks_mobile} mobile · {analytics.spotlight_clicks_desktop} desktop</div>
          </div>
        </div>
      )}

      {leaderPerf && leaderPerf.is_category_leader && (
        <LeaderPerformancePanel perf={leaderPerf} />
      )}

      {viewInsights && <ViewInsightsPanel insights={viewInsights} />}

      <div className="flex gap-1 border-b border-[#E8ECE9] mb-6 mt-8 flex-wrap">
        <button onClick={() => setTab("listings")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "listings" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="tab-listings">
          Listings ({listings.length})
        </button>
        <button onClick={() => setTab("products")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "products" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="tab-products">
          Products ({products.length})
        </button>
        <button onClick={() => setTab("rfqs")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "rfqs" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="tab-rfqs">
          <Inbox className="w-4 h-4 inline -mt-0.5" /> RFQ Inbox ({rfqs.length})
        </button>
        {limits && (limits.tier !== "free" || user?.role === "admin") && (
          <button onClick={() => setTab("credits")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "credits" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="tab-credits">
            Quote Credits
          </button>
        )}
        {limits && limits.can_add_brand_profile && (
          <button onClick={() => setTab("brands")} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "brands" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="tab-brands">
            Brands ({brands.length})
          </button>
        )}
        <button onClick={() => setTab("email-prefs")} className={`px-4 py-2 text-sm border-b-2 -mb-px ml-auto ${tab === "email-prefs" ? "border-[#264839] text-[#264839]" : "border-transparent text-[#7D8C85]"}`} data-testid="tab-email-prefs">
          Email preferences
        </button>
      </div>

      {tab === "listings" && (
        <div className="space-y-3">
          {listings.length === 0 ? (
            <div className="bg-[#F0EFEA] p-12 text-center text-[#7D8C85] rounded-sm">No listings yet. Create your first to start receiving RFQs.</div>
          ) : (
            listings.map((l) => (
              <div key={l.id} className="bg-white border border-[#E8ECE9] p-4 flex items-center gap-4" data-testid={`my-listing-${l.id}`}>
                <div className="w-20 h-20 bg-[#E8ECE9] flex-shrink-0 overflow-hidden">
                  {l.image_url ? <img src={absUrl(l.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-[#C4CFC9] m-auto mt-7" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link to={listingPath(l)} className="font-serif text-lg text-[#11211A] hover:text-[#264839] truncate">{l.title}</Link>
                    {l.tier !== "free" && <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 bg-[#264839] text-white">{l.tier}</span>}
                    {l.is_featured && <Star className="w-3 h-3 text-[#B35C4A] fill-current" />}
                  </div>
                  <div className="text-xs text-[#7D8C85] mt-1">{[l.city, l.country].filter(Boolean).join(", ") || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  {l.tier === "free" && pricing.length > 0 && (
                    <select onChange={(e) => e.target.value && upgrade(l.id, e.target.value)} className="text-xs border border-[#C4CFC9] px-2 py-1 rounded-sm" data-testid={`upgrade-${l.id}`}>
                      <option value="">Upgrade…</option>
                      {pricing.map((p) => <option key={p.id} value={p.id}>{p.label} — ${p.amount}</option>)}
                    </select>
                  )}
                  <button onClick={() => { setEditListing(l); setShowForm(true); }} className="text-xs px-2.5 py-1 border border-[#C4CFC9] text-[#4A5550] hover:border-[#264839] hover:text-[#264839] transition-colors" data-testid={`edit-listing-${l.id}`}>
                    Edit
                  </button>
                  <button onClick={() => del(l.id)} className="text-[#7D8C85] hover:text-red-600 p-1" data-testid={`delete-${l.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "products" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs text-[#7D8C85]">
              {limits ? `${limits.usage.products} / ${limits.limits.products === 999 ? "unlimited" : limits.limits.products} products used on the ${limits.tier_label} plan` : ""}
            </div>
            {limits && limits.limits.products > 0 && (
              <button onClick={() => { setEditProduct(null); setShowProductForm(true); }} disabled={!limits.can_add_product} title={!limits.can_add_product ? "Product limit reached — upgrade to add more." : ""} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="new-product-btn">
                <Plus className="w-4 h-4" /> New product
              </button>
            )}
          </div>

          {limits && limits.limits.products === 0 ? (
            <div className="bg-[#F0EFEA] p-10 text-center rounded-sm" data-testid="products-locked">
              <div className="tracking-overline mb-2">Locked on the {limits.tier_label} plan</div>
              <div className="font-serif text-xl text-[#11211A] mb-3">Individual product pages are a Catalogue Growth / Category Leader / Enterprise feature.</div>
              <p className="text-sm text-[#4A5550] max-w-md mx-auto mb-6">Upgrade to Catalogue Growth ($79/mo) to add up to 999 products with individual pages, gallery images and detailed specifications.</p>
              <Link to="/pricing" className="btn-primary inline-flex">See plans</Link>
            </div>
          ) : products.length === 0 ? (
            <div className="bg-[#F0EFEA] p-12 text-center text-[#7D8C85] rounded-sm">No products yet. Add your first showcase product — buyers will see it on your supplier profile.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.id} className="bg-white border border-[#E8ECE9] flex flex-col" data-testid={`my-product-${p.id}`}>
                  <div className="aspect-[4/3] bg-[#F0EFEA] overflow-hidden">
                    {p.image_url ? <img src={absUrl(p.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#C4CFC9] text-xs">No image</div>}
                  </div>
                  <div className="p-4 flex-1">
                    <div className="font-serif text-lg text-[#11211A]">{p.title}</div>
                    {(p.price_min || p.price_max) && <div className="text-xs text-[#4A5550] mt-1">${p.price_min || 0}{p.price_max ? ` – $${p.price_max}` : ""} {p.currency}</div>}
                  </div>
                  <div className="border-t border-[#E8ECE9] p-3 flex items-center justify-between">
                    <Link to={`/product/${p.id}`} className="text-xs text-[#264839] hover:underline">View</Link>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditProduct(p); setShowProductForm(true); }} className="text-xs text-[#4A5550] hover:text-[#264839]" data-testid={`edit-product-${p.id}`}>Edit</button>
                      <button onClick={() => delProduct(p.id)} className="text-xs text-[#7D8C85] hover:text-red-600" data-testid={`delete-product-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "rfqs" && (
        <div className="space-y-3" data-testid="rfq-list">
          {rfqs.length === 0 ? (
            <div className="bg-[#F0EFEA] p-12 text-center text-[#7D8C85] rounded-sm">No RFQs yet. Once customers request quotes, they'll appear here and in your email inbox.</div>
          ) : (
            rfqs.map((r) => (
              <div key={r.id} className="bg-white border border-[#E8ECE9] p-5" data-testid={`rfq-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-[#11211A] flex items-center gap-2 flex-wrap">
                      {r.listing_title}
                      {r.source === "campaign" && <span className="text-[9px] uppercase tracking-[0.18em] font-semibold bg-[#FFF4D6] text-[#8A6400] px-2 py-0.5" data-testid={`rfq-campaign-badge-${r.id}`}>Get-quotes campaign</span>}
                    </div>
                    <div className="text-xs text-[#7D8C85] mt-1">
                      From <span className="text-[#11211A]">{r.customer_name}</span> ({r.customer_email}) {r.customer_company && `· ${r.customer_company}`}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <a href={`mailto:${r.customer_email}?subject=Re: ${encodeURIComponent(r.listing_title)}`} className="text-xs text-[#264839] flex items-center gap-1 whitespace-nowrap">
                      <Mail className="w-3 h-3" /> Reply
                    </a>
                    {r.replied_at
                      ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#E8ECE9] text-[#264839] px-2 py-0.5" data-testid={`rfq-replied-${r.id}`}><Check className="w-3 h-3" /> Replied{typeof r.response_hours === "number" ? ` · ${r.response_hours}h` : ""}</span>
                      : <button type="button" onClick={() => markRfqReplied(r)} className="text-[10px] uppercase tracking-widest font-semibold border border-[#264839] text-[#264839] px-2.5 py-1 hover:bg-[#264839] hover:text-white transition" data-testid={`rfq-mark-replied-${r.id}`}>I've replied</button>}
                  </div>
                </div>
                {(r.quantity || r.required_by) && (
                  <div className="flex gap-4 mt-2 text-xs text-[#4A5550]">
                    {r.quantity && <span>Qty: {r.quantity}</span>}
                    {r.required_by && <span>By: {r.required_by}</span>}
                  </div>
                )}
                <div className="mt-3 text-sm text-[#1C221F] whitespace-pre-wrap leading-relaxed border-l-2 border-[#264839] pl-4">{r.notes}</div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.15em] text-[#7D8C85]">
                  Email: {r.email_status}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "credits" && limits && (
        <QuoteCreditsPanel credits={credits} limits={limits} />
      )}

      {tab === "brands" && limits && limits.can_add_brand_profile && (
        <BrandProfilesPanel brands={brands} onChange={load} />
      )}

      {tab === "email-prefs" && (
        <div className="max-w-2xl" data-testid="dashboard-email-prefs" ref={(el) => { if (el && searchParams.get("tab") === "email-prefs" && !el.dataset.scrolled) { el.dataset.scrolled = "1"; setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150); } }}>
          <h2 className="font-serif text-xl text-[#11211A] mb-1">Email preferences</h2>
          <p className="text-sm text-[#7D8C85] mb-4">Choose which reminder emails you want. Account, password and quote-request emails are always sent.</p>
          <EmailPreferences load={() => api.get("/me/email-prefs").then(({ data }) => data)} save={(body) => api.put("/me/email-prefs", body).then(({ data }) => data)} />
        </div>
      )}

      {showForm && <ListingForm listing={editListing} onClose={() => { setShowForm(false); setEditListing(null); }} onSaved={load} brands={brands} />}
      {showProductForm && <ProductForm product={editProduct} onClose={() => { setShowProductForm(false); setEditProduct(null); }} onSaved={load} brands={brands} limits={limits} />}
      <BulkUploadModal open={showBulk} onClose={() => setShowBulk(false)} onDone={load} />
    </div>
  );
}


function PlanBanner({ limits, credits, onBillingPortal, onChangePlan, onCancelPlan }) {
  const bg = limits.tier === "enterprise"
    ? "bg-[#B35C4A] text-white"
    : limits.tier === "leader"
      ? "bg-[#264839] text-white"
      : limits.tier === "growth"
        ? "bg-[#3E5E4E] text-white"
        : "bg-[#F0EFEA] text-[#11211A]";
  const trialDaysLeft = limits.trial_ends_at ? Math.max(0, Math.ceil((new Date(limits.trial_ends_at) - new Date()) / 86400000)) : null;
  const isUnlimited = (v) => v >= 999;
  const monthLimit = limits.limits.rfq_monthly || 0;
  const monthUsed = limits.usage.rfq_month_count || 0;
  const overLimit = monthLimit > 0 && monthUsed > monthLimit;
  const subStatus = limits.subscription_status || "";
  const statusPill = subStatus && limits.tier !== "free" ? (
    <span
      className={`inline-block ml-2 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm ${
        subStatus === "active" || subStatus === "trialing"
          ? "bg-emerald-100 text-emerald-800"
          : subStatus === "past_due"
            ? "bg-amber-100 text-amber-900"
            : "bg-rose-100 text-rose-900"
      }`}
      data-testid="subscription-status-pill"
    >
      {subStatus.replace("_", " ")}
    </span>
  ) : null;
  return (
    <div className={`${bg} px-5 py-4 flex flex-wrap items-center gap-4 justify-between`} data-testid="plan-banner">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Your plan</div>
        <div className="font-serif text-xl" data-testid="plan-tier-label">
          {limits.tier_label}
          
          {limits.is_category_leader && <span className="text-xs ml-2 opacity-80">★ Category Leader</span>}
          {statusPill}
        </div>
        {trialDaysLeft !== null && <div className="text-xs opacity-90 mt-1">Trial: {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} remaining</div>}
        {subStatus === "past_due" && <div className="text-xs opacity-90 mt-1" data-testid="past-due-warning">Payment failed — update your card to keep your plan active.</div>}
      </div>
      <div className="flex gap-6 text-xs flex-wrap">
        <div><div className="opacity-70">Listings</div><div className="text-lg">{limits.usage.listings} / {isUnlimited(limits.limits.categories) ? "∞" : limits.limits.categories}</div></div>
        <div><div className="opacity-70">Products</div><div className="text-lg">{limits.usage.products} / {isUnlimited(limits.limits.products) ? "∞" : limits.limits.products}</div></div>
        <div><div className="opacity-70">Gallery / item</div><div className="text-lg">{isUnlimited(limits.limits.gallery) ? "∞" : limits.limits.gallery}</div></div>
        <div data-testid="plan-rfq-counter">
          <div className="opacity-70">RFQs this month</div>
          <div className={`text-lg ${overLimit ? "text-[#F0EFEA]" : ""}`}>
            {monthUsed} / {monthLimit}
            {overLimit && <span className="text-xs ml-1 opacity-90">(over)</span>}
          </div>
        </div>
        {limits.tier !== "free" && (
          <div data-testid="plan-credits">
            <div className="opacity-70">Quote credits</div>
            <div className="text-lg">{credits ?? limits.quote_credits ?? 0}</div>
          </div>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        {limits.has_stripe_customer && limits.tier !== "free" && (
          <button
            type="button"
            onClick={onBillingPortal}
            className={`${limits.tier === "free" ? "btn-secondary" : "border border-current px-4 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-white/10 transition"}`}
            data-testid="manage-subscription-btn"
          >
            Manage subscription
          </button>
        )}
        {limits.tier !== "free" && (
          <button
            type="button"
            onClick={onChangePlan}
            className="border border-current px-4 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-white/10 transition"
            data-testid="change-plan-btn"
          >
            Change plan
          </button>
        )}
        {limits.tier !== "free" && limits.subscription_status === "active" && (
          <button
            type="button"
            onClick={onCancelPlan}
            className="border border-current px-4 py-2 text-xs uppercase tracking-widest font-semibold opacity-70 hover:opacity-100 hover:bg-white/10 transition"
            data-testid="cancel-plan-btn"
          >
            Cancel
          </button>
        )}
        {limits.tier !== "enterprise" && (
          <Link to="/pricing" className={`${limits.tier === "free" ? "btn-primary" : "btn-secondary"}`} data-testid="upgrade-plan-cta">Upgrade plan</Link>
        )}
      </div>
    </div>
  );
}


function BrandSwitcher({ brands, activeId, onChange }) {
  return (
    <div className="flex items-center gap-2" data-testid="brand-switcher">
      <span className="text-[10px] uppercase tracking-widest text-[#7D8C85] hidden sm:inline">Brand</span>
      <select
        value={activeId}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 border border-[#C4CFC9] bg-white text-sm text-[#11211A] rounded-sm"
        data-testid="brand-switcher-select"
      >
        <option value="">All brands (parent view)</option>
        {brands.map((b) => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </select>
    </div>
  );
}

function EnterpriseWelcomeModal({ onboarding }) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="enterprise-welcome-modal">
      <div className="bg-white max-w-lg w-full p-8 border-t-4 border-[#264839]">
        <div className="tracking-overline text-[#B35C4A]">Welcome to Enterprise</div>
        <h2 className="font-serif text-3xl text-[#11211A] mt-2">Your white-glove setup is ready.</h2>
        <p className="text-sm text-[#4A5550] mt-4 leading-relaxed">
          Follow the 5-step guide to configure your parent company, add your first brand, invite your team and bulk-load your catalogue. Progress saves as you go.
        </p>
        <ul className="mt-5 text-sm text-[#4A5550] leading-relaxed pl-5 list-decimal space-y-1">
          <li>Master profile — parent company & HQ</li>
          <li>Add your first brand</li>
          <li>Linking logic — parent-child hierarchy</li>
          <li>User permissions — invite your team</li>
          <li>Bulk SKU upload — CSV template & API docs</li>
        </ul>
        <div className="mt-6 flex items-center justify-between">
          <div className="text-xs text-[#7D8C85]">Progress: {onboarding.step_completed}/{onboarding.total_steps} steps done</div>
          <button
            type="button"
            onClick={() => navigate("/dashboard/enterprise-setup")}
            className="btn-primary"
            data-testid="enterprise-welcome-start"
          >Start setup →</button>
        </div>
      </div>
    </div>
  );
}

function LeaderPerformancePanel({ perf }) {
  const today = perf?.today || {};
  const month = perf?.this_month || {};
  const guarantee = perf?.guarantee || 120;
  const monthPct = Math.min(100, Math.round(((month.total_impressions || 0) / guarantee) * 100));
  const onTrack = !!month.on_track;
  const rfqsToday = today.rfqs_received || 0;

  return (
    <div className="mt-6 bg-[#F0EFEA] border border-[#264839]/25 p-5" data-testid="leader-performance-panel">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="tracking-overline text-[#B35C4A]">Category Leader placement</div>
          <div className="font-serif text-xl text-[#11211A]">Your premium front-page performance</div>
        </div>
        <div className="text-[10px] tracking-widest uppercase text-[#7D8C85]">Daily rotation · 8 slots × 3h</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E8ECE9] p-5" data-testid="leader-today-block">
          <div className="tracking-overline mb-2">Today's Performance · {today.date}</div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">Front-page impressions</div>
              <div className="font-serif text-3xl text-[#11211A] leading-none mt-1" data-testid="leader-today-impressions">{today.front_page_impressions || 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">Position 1 time</div>
              <div className="font-serif text-3xl text-[#11211A] leading-none mt-1" data-testid="leader-today-pos1">{today.position_1_hours || 0}<span className="text-sm text-[#7D8C85] ml-1">h</span></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">RFQs received</div>
              <div className="font-serif text-3xl text-[#11211A] leading-none mt-1" data-testid="leader-today-rfqs">{rfqsToday}</div>
            </div>
          </div>
          <div className="mt-3 text-xs text-[#7D8C85]">
            You've held Position 1 during {(today.position_1_slots || []).length} of 8 slots today.
          </div>
        </div>

        <div className="bg-white border border-[#E8ECE9] p-5" data-testid="leader-month-block">
          <div className="tracking-overline mb-2">This Month · {month.month}</div>
          <div className="flex items-baseline gap-2">
            <div className="font-serif text-3xl text-[#11211A] leading-none" data-testid="leader-month-impressions">{month.total_impressions || 0}</div>
            <div className="text-sm text-[#7D8C85]">/ {guarantee} guaranteed</div>
          </div>
          <div className="mt-3 h-2 w-full bg-[#F0EFEA] rounded-sm overflow-hidden" data-testid="leader-month-progress">
            <div
              className={`h-full ${onTrack ? "bg-[#264839]" : "bg-[#B35C4A]"}`}
              style={{ width: `${monthPct}%` }}
            />
          </div>
          <div className="mt-2 text-xs">
            {onTrack ? (
              <span className="text-[#264839]" data-testid="leader-month-status">On track — guarantee met.</span>
            ) : (
              <span className="text-[#B35C4A]" data-testid="leader-month-status">{guarantee - (month.total_impressions || 0)} more impressions to hit your 120/month guarantee.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function QuoteCreditsPanel({ credits, limits }) {
  const monthUsed = credits?.rfq_month_count ?? limits.usage.rfq_month_count ?? 0;
  const monthLimit = credits?.rfq_month_limit ?? limits.limits.rfq_monthly ?? 0;
  const balance = credits?.quote_credits ?? limits.quote_credits ?? 0;
  const over = credits?.over_limit;
  const txns = credits?.transactions || [];
  const [packs, setPacks] = useState([]);
  const [buying, setBuying] = useState("");
  const [autoReload, setAutoReload] = useState({
    enabled: !!credits?.auto_reload?.enabled,
    pack_id: credits?.auto_reload?.pack_id || "pack_50",
    threshold: credits?.auto_reload?.threshold ?? 5,
  });
  const [savingAR, setSavingAR] = useState(false);

  useEffect(() => {
    api.get("/payments/credit-packs")
      .then(({ data }) => setPacks(data || []))
      .catch(() => setPacks([]));
  }, []);

  // Keep local auto-reload state synced when parent refetches credits.
  useEffect(() => {
    if (credits?.auto_reload) {
      setAutoReload({
        enabled: !!credits.auto_reload.enabled,
        pack_id: credits.auto_reload.pack_id || "pack_50",
        threshold: credits.auto_reload.threshold ?? 5,
      });
    }
  }, [credits?.auto_reload?.enabled, credits?.auto_reload?.pack_id, credits?.auto_reload?.threshold]);

  const buyPack = async (packId) => {
    setBuying(packId);
    try {
      const { data } = await api.post("/payments/credit-checkout", {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed — please try again.");
      setBuying("");
    }
  };

  const scrollToPacks = () => {
    const el = document.querySelector('[data-testid="credit-packs-panel"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const saveAutoReload = async (next) => {
    const desired = next || autoReload;
    setSavingAR(true);
    try {
      const { data } = await api.put("/supplier/auto-reload", {
        enabled:   !!desired.enabled,
        pack_id:   desired.pack_id,
        threshold: desired.threshold,
      });
      // Backend echoes the persisted threshold (may clamp) — trust it.
      setAutoReload({ ...desired, threshold: data.auto_reload?.threshold ?? desired.threshold });
      toast.success(desired.enabled ? "Auto-reload settings saved." : "Auto-reload turned off.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save your preference — please try again.");
    } finally {
      setSavingAR(false);
    }
  };

  const toggleEnabled = () => saveAutoReload({ ...autoReload, enabled: !autoReload.enabled });
  const changePack = (pack_id) => {
    const next = { ...autoReload, pack_id };
    if (autoReload.enabled) saveAutoReload(next);
    else setAutoReload(next);
  };
  // Iter62 · inline editable threshold.
  const [editingThreshold, setEditingThreshold] = useState(false);
  const [thresholdDraft, setThresholdDraft] = useState(autoReload.threshold ?? 5);
  useEffect(() => { setThresholdDraft(autoReload.threshold ?? 5); }, [autoReload.threshold]);
  const commitThreshold = () => {
    const clamped = Math.max(1, Math.min(200, parseInt(thresholdDraft, 10) || 5));
    setEditingThreshold(false);
    saveAutoReload({ ...autoReload, threshold: clamped });
  };

  const lowBalance = balance <= (autoReload.threshold ?? 5);
  const selectedPack = packs.find((p) => p.id === autoReload.pack_id) || packs[1] || {};

  return (
    <div className="space-y-6" data-testid="credits-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E8ECE9] p-5">
          <div className="tracking-overline mb-2">This month's RFQs</div>
          <div className="font-serif text-3xl text-[#11211A]" data-testid="credits-month-usage">{monthUsed} / {monthLimit}</div>
          <div className="text-xs text-[#7D8C85] mt-2">
            {over ? "Over your plan ceiling — extra RFQs draw from credits." : "Within plan quota."}
          </div>
        </div>
        <div className={`bg-white border p-5 ${lowBalance ? "border-[#B35C4A]" : "border-[#E8ECE9]"}`}>
          <div className="flex items-baseline justify-between mb-2">
            <div className="tracking-overline">Quote credit balance</div>
            {lowBalance && (
              <div className="text-[10px] tracking-widest uppercase text-[#B35C4A]" data-testid="credits-low-flag">Low balance</div>
            )}
          </div>
          <div className="font-serif text-3xl text-[#11211A]" data-testid="credits-balance">{balance}</div>
          <div className="flex items-center justify-between mt-3 gap-3">
            <div className="text-xs text-[#7D8C85]">No expiry. Consumed only after your monthly ceiling.</div>
            <button
              type="button"
              onClick={scrollToPacks}
              className="btn-primary text-xs whitespace-nowrap"
              data-testid="manual-topup-btn"
            >
              Manual Top-Up
            </button>
          </div>
        </div>
      </div>

      <div className="bg-[#F0EFEA] border border-[#264839]/20 p-5" data-testid="credit-packs-panel">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="tracking-overline text-[#B35C4A]">Top up instantly</div>
            <div className="font-serif text-xl text-[#11211A]">Buy a Quote Credit Pack</div>
          </div>
          <div className="text-xs text-[#4A5550] italic hidden md:block">No expiry · Secure Stripe checkout</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {packs.map((pack) => (
            <div
              key={pack.id}
              className={`relative bg-white p-5 border ${pack.badge ? "border-[#264839]" : "border-[#E8ECE9]"} flex flex-col`}
              data-testid={`credit-pack-${pack.id}`}
            >
              {pack.badge && (
                <div className="absolute -top-3 left-4 bg-[#264839] text-white text-[10px] tracking-widest uppercase px-2 py-1">
                  {pack.badge}
                </div>
              )}
              <div className="tracking-overline text-[#7D8C85] mb-2">{pack.label}</div>
              <div className="font-serif text-3xl text-[#11211A] leading-none">{pack.credits}<span className="text-sm text-[#7D8C85] ml-1">credits</span></div>
              <div className="mt-2 text-sm text-[#4A5550]">
                <span className="font-semibold text-[#11211A]">${pack.amount.toFixed(0)}</span>
                <span className="text-xs text-[#7D8C85] ml-1">· ${(pack.amount / pack.credits).toFixed(2)}/credit</span>
              </div>
              <button
                type="button"
                onClick={() => buyPack(pack.id)}
                disabled={!!buying}
                className="btn-primary mt-4 text-xs justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid={`buy-credit-pack-${pack.id}`}
              >
                {buying === pack.id ? "Redirecting…" : `Buy ${pack.credits} credits`}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#264839]/30 p-5" data-testid="auto-reload-panel">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <label className="flex items-start gap-3 cursor-pointer flex-1">
            <input
              type="checkbox"
              checked={autoReload.enabled}
              onChange={toggleEnabled}
              disabled={savingAR}
              className="mt-1 h-4 w-4 accent-[#264839] cursor-pointer"
              data-testid="auto-reload-toggle"
            />
            <div>
              <div className="font-serif text-lg text-[#11211A] leading-tight" data-testid="auto-reload-headline">
                Automatically refill my balance when it drops below{" "}
                {editingThreshold ? (
                  <span className="inline-flex items-baseline gap-1 align-baseline">
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={thresholdDraft}
                      onChange={(e) => setThresholdDraft(e.target.value)}
                      onBlur={commitThreshold}
                      onKeyDown={(e) => { if (e.key === "Enter") commitThreshold(); if (e.key === "Escape") { setEditingThreshold(false); setThresholdDraft(autoReload.threshold ?? 5); } }}
                      autoFocus
                      className="w-16 px-1.5 py-0.5 border border-[#264839] text-[#264839] font-serif text-lg text-center bg-white"
                      data-testid="auto-reload-threshold-input"
                    /> credits.
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingThreshold(true); }}
                    className="underline decoration-dotted underline-offset-4 hover:text-[#264839]"
                    data-testid="auto-reload-threshold-edit"
                    title="Click to change threshold"
                  >
                    {autoReload.threshold} credits
                  </button>
                )} {!editingThreshold && "."}
                {selectedPack?.amount && (
                  <span className="text-[11px] text-[#7D8C85] ml-2 align-middle" data-testid="auto-reload-dollar-hint">
                    ≈ ${selectedPack.amount.toFixed(0)} per top-up
                  </span>
                )}
              </div>
              <div className="text-xs text-[#4A5550] mt-1 leading-relaxed">
                <strong className="text-[#264839]">Supplier benefit:</strong> stay reachable 24/7. If we have a card on file, we'll charge it and top you up in seconds — otherwise we'll email a one-click top-up link.
              </div>
              {credits?.auto_reload?.has_saved_card ? (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#264839] bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm" data-testid="card-on-file-badge">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Card on file — instant refills
                </div>
              ) : (
                <div className="mt-2 text-[11px] text-[#7D8C85] italic" data-testid="no-card-hint">
                  No card saved yet. Buy any pack once and we'll securely save it for future one-tap refills.
                </div>
              )}
            </div>
          </label>
          <div className="flex items-center gap-2 md:pt-1" data-testid="auto-reload-pack-selector">
            <span className="text-xs text-[#7D8C85] uppercase tracking-wider">Refill with</span>
            <select
              value={autoReload.pack_id}
              onChange={(e) => changePack(e.target.value)}
              disabled={savingAR}
              className="px-3 py-1.5 border border-[#C4CFC9] text-sm bg-white"
              data-testid="auto-reload-pack-select"
            >
              {packs.map((p) => (
                <option key={p.id} value={p.id}>{p.credits} credits · ${p.amount.toFixed(0)}</option>
              ))}
            </select>
          </div>
        </div>
        {autoReload.enabled && (
          <div className="mt-3 pt-3 border-t border-[#F0EFEA] text-xs text-[#4A5550]" data-testid="auto-reload-status-on">
            Auto-reload is <strong className="text-[#264839]">ON</strong> —{" "}
            {credits?.auto_reload?.has_saved_card ? (
              <>we'll auto-charge your saved card for {selectedPack?.credits ? `${selectedPack.credits} credits ($${selectedPack.amount?.toFixed?.(0)})` : "your selected pack"} the moment your balance hits {autoReload.threshold} or below.</>
            ) : (
              <>we'll email a checkout link when your balance hits {autoReload.threshold} or below{selectedPack?.credits ? ` for ${selectedPack.credits} credits ($${selectedPack.amount?.toFixed?.(0)}).` : "."}</>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E8ECE9]">
        <div className="px-5 py-3 border-b border-[#E8ECE9] tracking-overline">Recent transactions</div>
        {txns.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#7D8C85]">No credit activity yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-[#7D8C85] uppercase tracking-wider">
              <tr><th className="text-left px-5 py-2">When</th><th className="text-left px-5 py-2">Source</th><th className="text-left px-5 py-2">Note</th><th className="text-right px-5 py-2">Δ</th></tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.id} className="border-t border-[#F0EFEA]" data-testid={`credit-txn-${t.id}`}>
                  <td className="px-5 py-2 text-[#4A5550]">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="px-5 py-2">{t.source || t.kind}</td>
                  <td className="px-5 py-2 text-[#4A5550]">{t.note || ""}</td>
                  <td className="px-5 py-2 text-right">{t.delta > 0 ? `+${t.delta}` : t.delta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function BrandProfilesPanel({ brands, onChange }) {
  const [form, setForm] = useState({ name: "", website: "", country_code: "", description: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/brand-profiles", form);
      toast.success("Brand profile created");
      setForm({ name: "", website: "", country_code: "", description: "" });
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this brand profile? Listings will be detached, not deleted.")) return;
    try {
      await api.delete(`/brand-profiles/${id}`);
      toast.success("Brand profile deleted");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="brands-panel">
      <form onSubmit={submit} className="bg-white border border-[#E8ECE9] p-5 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="brand-create-form">
        <input required placeholder="Brand name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border border-[#C4CFC9]" data-testid="brand-name-input" />
        <input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="px-3 py-2 border border-[#C4CFC9]" data-testid="brand-website-input" />
        <input placeholder="Country (AU/US/…)" value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })} maxLength={2} className="px-3 py-2 border border-[#C4CFC9]" data-testid="brand-country-input" />
        <button type="submit" disabled={saving} className="btn-primary justify-center disabled:opacity-60" data-testid="brand-create-submit">{saving ? "Saving…" : "Add brand"}</button>
        <textarea placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-4 px-3 py-2 border border-[#C4CFC9] text-sm" rows={2} data-testid="brand-description-input" />
      </form>

      {brands.length === 0 ? (
        <div className="bg-[#F0EFEA] p-10 text-center text-sm text-[#7D8C85]">No brands yet. Add your first — each brand can hold its own listings, products and gallery.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {brands.map((b) => (
            <div key={b.id} className="bg-white border border-[#E8ECE9] p-5 flex items-start justify-between gap-4" data-testid={`brand-${b.id}`}>
              <div className="min-w-0">
                <div className="font-serif text-lg text-[#11211A]">{b.name}</div>
                <div className="text-xs text-[#7D8C85] mt-1">
                  {b.country_code || "—"} · slug: <code>{b.slug}</code>
                </div>
                {b.website && <a href={b.website} className="text-xs text-[#264839] hover:underline" target="_blank" rel="noreferrer">{b.website}</a>}
                {b.description && <p className="text-xs text-[#4A5550] mt-2 leading-relaxed">{b.description}</p>}
              </div>
              <button onClick={() => del(b.id)} className="text-[#7D8C85] hover:text-red-600" data-testid={`brand-delete-${b.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ProductForm({ product, onClose, onSaved, brands = [], limits = null }) {
  const isEdit = !!product;
  const [tree, setTree] = useState([]);
  const [form, setForm] = useState(() => ({
    title: product?.title || "",
    description: product?.description || "",
    listing_id: product?.listing_id || "",
    category_id: product?.category_id || "",
    tags: (product?.tags || []).join(", "),
    image_url: product?.image_url || "",
    gallery: (product?.gallery || []).join("\n"),
    price_min: product?.price_min ?? "",
    price_max: product?.price_max ?? "",
    currency: product?.currency || "AUD",
    lead_time: product?.lead_time || "",
    moq: product?.moq || "",
    specs: JSON.stringify(product?.specs || {}, null, 2),
    brand_profile_id: product?.brand_profile_id || "",
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  useEffect(() => { api.get("/categories/tree").then(({ data }) => setTree(data)); }, []);

  const emptyForm = () => ({
    title: "", description: "", listing_id: form.listing_id, category_id: form.category_id,
    tags: "", image_url: "", gallery: "", price_min: "", price_max: "",
    currency: form.currency, lead_time: "", moq: "", specs: "{}",
    brand_profile_id: form.brand_profile_id,
  });

  const handleImageUpload = async (e, target = "image_url") => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "gallery") {
        setForm((f) => ({ ...f, gallery: (f.gallery ? f.gallery + "\n" : "") + data.url }));
      } else {
        setForm((f) => ({ ...f, image_url: data.url }));
      }
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const doSubmit = async (addAnother) => {
    setSaving(true);
    let specs = {};
    try { specs = JSON.parse(form.specs || "{}"); } catch { toast.error("Specs must be valid JSON"); setSaving(false); return; }
    const payload = {
      title: form.title,
      description: form.description,
      listing_id: form.listing_id || null,
      category_id: form.category_id || null,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      image_url: form.image_url,
      gallery: form.gallery.split("\n").map(l => l.trim()).filter(Boolean),
      price_min: form.price_min ? Number(form.price_min) : null,
      price_max: form.price_max ? Number(form.price_max) : null,
      currency: form.currency,
      lead_time: form.lead_time,
      moq: form.moq,
      specs,
      brand_profile_id: form.brand_profile_id || null,
    };
    try {
      if (isEdit) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      const nextCount = savedCount + (isEdit ? 0 : 1);
      setSavedCount(nextCount);
      onSaved();
      if (addAnother && !isEdit) {
        toast.success(`Product ${nextCount} saved · form cleared for the next SKU`);
        setForm(emptyForm());
      } else {
        toast.success(isEdit ? "Product updated" : "Product created");
        onClose();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save product");
    } finally { setSaving(false); }
  };

  const submit = (e) => { e.preventDefault(); doSubmit(false); };

  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="product-form-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between p-6 border-b border-[#E8ECE9]">
          <div>
            <div className="tracking-overline">{isEdit ? "Edit product" : "New product"}</div>
            <div className="font-serif text-xl">{isEdit ? product.title : "Add a product to your catalogue"}</div>
          </div>
          <button onClick={onClose} className="text-[#7D8C85] text-2xl leading-none" data-testid="product-form-close">×</button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-3">
          <input required placeholder="Product title" value={form.title} onChange={set("title")} className="col-span-2 px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="product-title" />
          <textarea required rows={3} placeholder="Description" value={form.description} onChange={set("description")} className="col-span-2 px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="product-description" />
          <select value={form.category_id} onChange={set("category_id")} className="px-3 py-2 border border-[#C4CFC9] bg-white rounded-sm" data-testid="product-category">
            <option value="">— Category —</option>
            {tree.map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.is_coming_soon ? " (pre-launch)" : ""}</option>
            ))}
          </select>
          <div>
            <input placeholder="Custom tags — comma-separated (e.g. hypoallergenic, oeko-tex, king)" value={form.tags} onChange={set("tags")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="product-tags" />
            {(() => {
              const currentTags = (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
              const maxTags = limits?.limits?.max_tags ?? null;
              const tier = limits?.tier || "free";
              const overLimit = maxTags != null && currentTags.length > maxTags;
              if (maxTags == null) return null;
              const isUnlimited = maxTags >= 1000;
              return (
                <div className={`text-[10px] mt-1 flex items-center gap-1.5 ${overLimit ? "text-[#B35C4A]" : "text-[#7D8C85]"}`} data-testid="product-tag-limit">
                  <span>
                    {isUnlimited ? `${currentTags.length} tag${currentTags.length === 1 ? "" : "s"} · Unlimited on ${tier.toString().charAt(0).toUpperCase() + tier.slice(1)}` : `${currentTags.length} / ${maxTags} tag${maxTags === 1 ? "" : "s"} used on ${tier.charAt(0).toUpperCase() + tier.slice(1)}`}
                  </span>
                  {!isUnlimited && (overLimit || currentTags.length >= maxTags) && (
                    <a href="/pricing" className="uppercase tracking-widest font-semibold text-[#B35C4A] hover:underline" data-testid="product-tag-upgrade">Upgrade to unlock →</a>
                  )}
                </div>
              );
            })()}
            {form.category_id && (() => {
              const parent = tree.find((p) => p.id === form.category_id);
              const suggestions = ((parent?.tag_filters) || []).filter((t) => t.tag).slice(0, 10);
              if (suggestions.length === 0) return null;
              const current = new Set((form.tags || "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean));
              return (
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid="product-tag-suggestions">
                  <span className="text-[10px] text-[#7D8C85] uppercase tracking-widest mr-1 self-center">Add:</span>
                  {suggestions.map((s) => {
                    const on = current.has(s.tag);
                    return (
                      <button
                        key={s.tag}
                        type="button"
                        disabled={on}
                        onClick={() => {
                          const next = (form.tags ? form.tags + ", " : "") + s.tag;
                          setForm({ ...form, tags: next });
                        }}
                        className={`text-[10px] px-2 py-0.5 border transition ${on ? "bg-[#264839] text-white border-[#264839] opacity-50 cursor-not-allowed" : "bg-white text-[#4A5550] border-[#C4CFC9] hover:border-[#264839] hover:text-[#264839]"}`}
                        data-testid={`suggest-tag-${s.tag}`}
                      >
                        + {s.label}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          {brands.length > 0 && (
            <div className="col-span-2" data-testid="product-brand-field">
              <label className="block text-xs text-[#7D8C85] mb-1">Brand (Enterprise — link this product to one of your brands)</label>
              <select value={form.brand_profile_id} onChange={set("brand_profile_id")} className="w-full px-3 py-2 border border-[#C4CFC9] bg-white rounded-sm" data-testid="product-brand-select">
                <option value="">— No brand —</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="col-span-2 border-t border-[#E8ECE9] pt-4">
            <label className="block text-xs font-medium text-[#4A5550] mb-2">Product photos — upload real images (JPEG/PNG/WebP, up to 10MB each)</label>
            <div className="flex flex-wrap gap-3 items-start">
              {form.image_url && (
                <div className="w-28 h-28 border border-[#E8ECE9] overflow-hidden relative" data-testid="product-primary-preview">
                  <img src={absUrl(form.image_url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="absolute top-1 right-1 bg-white/80 hover:bg-white text-[#B35C4A] text-xs px-1.5 py-0.5 rounded">×</button>
                  <div className="absolute bottom-0 left-0 right-0 bg-[#264839] text-white text-[9px] uppercase tracking-wider py-0.5 text-center">Primary</div>
                </div>
              )}
              {(form.gallery || "").split("\n").filter(Boolean).map((url, idx) => (
                <div key={idx} className="w-28 h-28 border border-[#E8ECE9] overflow-hidden relative" data-testid={`product-gallery-preview-${idx}`}>
                  <img src={absUrl(url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.split("\n").filter((_, i) => i !== idx).join("\n") }))} className="absolute top-1 right-1 bg-white/80 hover:bg-white text-[#B35C4A] text-xs px-1.5 py-0.5 rounded">×</button>
                </div>
              ))}
              <label className={`w-28 h-28 border-2 border-dashed border-[#C4CFC9] hover:border-[#264839] flex flex-col items-center justify-center text-[#7D8C85] hover:text-[#264839] cursor-pointer transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="product-upload-tile">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                <div className="text-[10px] uppercase tracking-wider mt-2">{uploading ? "Uploading" : (form.image_url ? "Add photo" : "Primary photo")}</div>
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleImageUpload(e, form.image_url ? "gallery" : "image_url")} data-testid="product-file-input" />
              </label>
            </div>
          </div>
          <input type="number" step="0.01" placeholder="Price min" value={form.price_min} onChange={set("price_min")} className="px-3 py-2 border border-[#C4CFC9] rounded-sm" />
          <input type="number" step="0.01" placeholder="Price max" value={form.price_max} onChange={set("price_max")} className="px-3 py-2 border border-[#C4CFC9] rounded-sm" />
          <input placeholder="Currency (AUD / USD…)" value={form.currency} onChange={set("currency")} className="px-3 py-2 border border-[#C4CFC9] rounded-sm uppercase" />
          <input placeholder="Lead time" value={form.lead_time} onChange={set("lead_time")} className="px-3 py-2 border border-[#C4CFC9] rounded-sm" />
          <input placeholder="MOQ" value={form.moq} onChange={set("moq")} className="col-span-2 px-3 py-2 border border-[#C4CFC9] rounded-sm" />
          <div className="col-span-2">
            <label className="block text-xs text-[#7D8C85] mb-1">Specifications — JSON, e.g. {"{"}"Material":"Cotton","GSM":600{"}"}</label>
            <textarea rows={4} value={form.specs} onChange={set("specs")} className="w-full px-3 py-2 border border-[#C4CFC9] rounded-sm text-xs font-mono" />
          </div>
          <div className="col-span-2 flex flex-wrap items-center justify-between gap-3 border-t border-[#E8ECE9] pt-4">
            <div className="text-xs text-[#7D8C85]">
              {savedCount > 0 && !isEdit && <span data-testid="product-saved-count">{savedCount} product{savedCount === 1 ? "" : "s"} saved this session</span>}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="btn-ghost">Done</button>
              {!isEdit && (
                <button type="button" onClick={() => doSubmit(true)} disabled={saving} className="btn-secondary flex items-center gap-1.5 disabled:opacity-50" data-testid="product-save-and-add">
                  <Plus className="w-4 h-4" /> {saving ? "Saving…" : "Save & Add Another"}
                </button>
              )}
              <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="product-submit">
                {saving ? "Saving…" : (isEdit ? "Save changes" : "Save & Close")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

